'use client';

import { Network, Plus } from 'lucide-react';
import { useState } from 'react';
import { EmptyState, ErrorState, LoadingState, PageHeader } from '@/components/shared';
import { Button, Card, Table, TableBody, TableHead, TableHeader, TableRow } from '@/components/ui';
import { usePartners } from '@/hooks/usePartners';
import { CreatePartnerDialog } from './CreatePartnerDialog';
import { PartnerTreeRows } from './PartnerTreeRows';

/**
 * The distribution chain, as a tree.
 *
 * An integrator holds the provider tokens and the pool; a sub-dealer sells on
 * the integrator's account and inherits its token. Both are rows in one table,
 * so there is nothing to migrate the first time someone sells through a third
 * level.
 */
export function PartnersPage() {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [parentForNew, setParentForNew] = useState<string | null>(null);
  const { data, isLoading, error } = usePartners();

  if (isLoading) return <LoadingState />;
  if (error) return <ErrorState title="Could not load partners" message={error.message} />;

  const partners = data?.data ?? [];

  const openCreate = (parentId: string | null) => {
    setParentForNew(parentId);
    setIsCreateOpen(true);
  };

  return (
    <>
      <PageHeader
        title="Partners"
        description="Integrators and their sub-dealers. An integrator holds the Flespi tokens and the device pool."
        actions={
          <Button onClick={() => openCreate(null)}>
            <Plus className="mr-2 h-4 w-4" />
            New integrator
          </Button>
        }
      />

      {partners.length === 0 ? (
        <EmptyState
          icon={Network}
          title="No partners yet"
          description="Create the integrator first; its token is what makes a device pool visible."
          action={
            <Button onClick={() => openCreate(null)}>
              <Plus className="mr-2 h-4 w-4" />
              Create integrator
            </Button>
          }
        />
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Partner</TableHead>
                <TableHead>Flespi token</TableHead>
                <TableHead>Pool</TableHead>
                <TableHead>Customers</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <PartnerTreeRows
                partners={partners}
                parentId={null}
                depth={0}
                onAddChild={openCreate}
              />
            </TableBody>
          </Table>
        </Card>
      )}

      <CreatePartnerDialog
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        partners={partners}
        parentId={parentForNew}
      />
    </>
  );
}
